/**
 * Comfort Modes Context
 * Manages accessibility and comfort preferences: contrast, typography, motion, color vision
 */

import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';

const ComfortModesContext = createContext();

const DEFAULT_COMFORT_MODES = {
  contrast: 'normal', // 'normal' | 'high' | 'maximum'
  typography: 100, // 100 | 110 | 125 | 150 (percentage)
  motion: 'full', // 'full' | 'reduced' | 'none'
  colorVision: 'normal', // 'normal' | 'protanopia' | 'deuteranopia' | 'tritanopia'
};

export const ComfortModesProvider = ({ children }) => {
  const [comfortModes, setComfortModes] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('comfortModes');
      return saved ? JSON.parse(saved) : DEFAULT_COMFORT_MODES;
    }
    return DEFAULT_COMFORT_MODES;
  });

  // Define applyComfortModes BEFORE useEffect that uses it (fixes TDZ)
  const applyComfortModes = useCallback((modes) => {
    const root = document.documentElement;

    // Contrast modes
    root.setAttribute('data-contrast', modes.contrast);

    // Typography scaling
    root.style.setProperty('--typography-scale', `${modes.typography / 100}`);

    // Motion modes
    root.setAttribute('data-motion', modes.motion);

    // Color vision presets
    root.setAttribute('data-color-vision', modes.colorVision);
  }, []);

  // Apply on mount and when comfortModes change
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('comfortModes', JSON.stringify(comfortModes));
    }
    applyComfortModes(comfortModes);
  }, [comfortModes, applyComfortModes]);

  const updateComfortMode = useCallback((key, value) => {
    setComfortModes((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const resetComfortModes = useCallback(() => {
    setComfortModes(DEFAULT_COMFORT_MODES);
  }, []);

  return (
    <ComfortModesContext.Provider value={{ comfortModes, updateComfortMode, resetComfortModes }}>
      {children}
    </ComfortModesContext.Provider>
  );
};

export const useComfortModes = () => useContext(ComfortModesContext);

